import {
  IconAlignCenter,
  IconAlignJustified,
  IconAlignLeft,
  IconAlignRight,
  IconBold,
  IconCode,
  IconHighlight,
  IconItalic,
  IconList,
  IconListNumbers,
  IconUnderline,
} from "@tabler/icons-react";
import { Extension, mergeAttributes } from "@tiptap/core";
import { Placeholder } from "@tiptap/extensions";
import { EditorContent, useEditor, useEditorState } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import type React from "react";
import { type RefObject, useEffect, useRef, useState } from "react";
import { TemplateStringBlock } from "@/components/Admin/AdminDocumentPreferences/utils/TipTapTemplateStringExtension";
import "./styles.scss";
import Highlight from "@tiptap/extension-highlight";
import { ListItem, OrderedList } from "@tiptap/extension-list";
import Paragraph from "@tiptap/extension-paragraph";
import TextAlign from "@tiptap/extension-text-align";
import Underline from "@tiptap/extension-underline";
import { useAuth } from "@/hooks/useAuth";
import useConfig, { HIGHLIGHT_FF } from "@/hooks/useConfig";
import useScrollHiddenTiptap from "@/hooks/useScrollHiddenTiptap";
import { FlattenAndFilterOnPaste } from "./CustomPasteFilterFlattener";

type MenuBarProps = {
  editor: ReturnType<typeof useEditor>;
  menuBarRef: RefObject<HTMLDivElement>;
  showMenuOptions?: boolean;
  showHighlight?: boolean;
  showRightAlign?: boolean;
  showTemplateStringBlock?: boolean;
};

// Custom extensions
const BlurOnEnter = Extension.create({
  name: "blurOnEnter",
  addKeyboardShortcuts() {
    return {
      Enter: ({ editor }) => {
        try {
          if (editor.view?.dom) {
            editor.view.dom.blur();
          }
        } catch {
          // view may be unmounted during teardown
        }
        return true;
      },
    };
  },
});

const BlurOnShiftEnter = Extension.create({
  name: "blurOnShiftEnter",
  addKeyboardShortcuts() {
    return {
      "Shift-Enter": ({ editor }) => {
        try {
          if (editor.view?.dom) {
            editor.view.dom.blur();
          }
        } catch {
          // view may be unmounted during teardown
        }
        return true;
      },
    };
  },
});

const TabIndent = Extension.create({
  name: "tabIndent",
  addKeyboardShortcuts() {
    return {
      Tab: ({ editor }) => {
        try {
          if (editor.view) {
            editor.chain().focus().insertContent("\t").run();
          }
        } catch {
          // view may be unmounted during teardown
        }
        return true;
      },
    };
  },
});

const MenuBar = ({
  editor,
  menuBarRef,
  showMenuOptions,
  showHighlight = false,
  showRightAlign = false,
  showTemplateStringBlock = false,
}: MenuBarProps) => {
  const activeStates = useEditorState({
    editor,
    selector: (snapshot) => ({
      bold: snapshot.editor?.isActive("bold") ?? false,
      italic: snapshot.editor?.isActive("italic") ?? false,
      underline: snapshot.editor?.isActive("underline") ?? false,
      highlight: snapshot.editor?.isActive("highlight") ?? false,
      bulletList: snapshot.editor?.isActive("bulletList") ?? false,
      orderedList: snapshot.editor?.isActive("orderedList") ?? false,
      alignLeft: snapshot.editor?.isActive({ textAlign: "left" }) ?? false,
      alignCenter: snapshot.editor?.isActive({ textAlign: "center" }) ?? false,
      alignRight: snapshot.editor?.isActive({ textAlign: "right" }) ?? false,
      alignJustify:
        snapshot.editor?.isActive({ textAlign: "justify" }) ?? false,
      templateString: snapshot.editor?.isActive("templateString") ?? false,
    }),
  });

  if (!editor || !editor.view) {
    return null;
  }

  const createButton = (
    action: () => void,
    isActive: boolean,
    icon: React.ReactNode,
  ) => (
    <button
      type="button"
      onClick={action}
      className={isActive ? "is-active" : ""}
    >
      {icon}
    </button>
  );

  return (
    <div className="menubar-container" ref={menuBarRef}>
      {createButton(
        () => {
          if (editor.view) {
            editor.chain().toggleBold().run();
          }
        },
        activeStates.bold,
        <IconBold size={24} aria-label="Bold" />,
      )}
      {createButton(
        () => {
          if (editor.view) {
            editor.chain().focus().toggleItalic().run();
          }
        },
        activeStates.italic,
        <IconItalic size={24} aria-label="Italic" />,
      )}
      {createButton(
        () => {
          if (editor.view) {
            editor.chain().focus().toggleUnderline().run();
          }
        },
        activeStates.underline,
        <IconUnderline size={24} aria-label="Underline" />,
      )}
      {showMenuOptions && (
        <>
          {createButton(
            () => {
              if (editor.view) {
                editor.chain().focus().toggleBulletList().run();
              }
            },
            activeStates.bulletList,
            <IconList size={24} aria-label="Bullet list" />,
          )}
          {createButton(
            () => {
              if (editor.view) {
                editor.chain().focus().toggleOrderedList().run();
              }
            },
            activeStates.orderedList,
            <IconListNumbers size={24} aria-label="Numbered list" />,
          )}
          {createButton(
            () => {
              if (editor.view) {
                editor.chain().focus().setTextAlign("left").run();
              }
            },
            activeStates.alignLeft,
            <IconAlignLeft size={24} aria-label="Align left" />,
          )}
          {createButton(
            () => {
              if (editor.view) {
                editor.chain().focus().setTextAlign("center").run();
              }
            },
            activeStates.alignCenter,
            <IconAlignCenter size={24} aria-label="Align center" />,
          )}
          {showRightAlign
            ? createButton(
                () => {
                  if (editor.view) {
                    editor.chain().focus().setTextAlign("right").run();
                  }
                },
                activeStates.alignRight,
                <IconAlignRight size={24} aria-label="Align right" />,
              )
            : null}
          {createButton(
            () => {
              if (editor.view) {
                editor.chain().focus().setTextAlign("justify").run();
              }
            },
            activeStates.alignJustify,
            <IconAlignJustified size={24} aria-label="Align justify" />,
          )}
          {showTemplateStringBlock
            ? createButton(
                () => {
                  if (editor.view) {
                    editor.chain().focus().toggleCustomSection().run();
                  }
                },
                activeStates.templateString,
                <IconCode size={24} aria-label="Template string block" />,
              )
            : null}
        </>
      )}
      {showHighlight
        ? createButton(
            () => {
              if (editor.view) {
                editor.chain().focus().toggleHighlight().run();
              }
            },
            activeStates.highlight,
            <IconHighlight size={24} aria-label="Highlight" />,
          )
        : null}
    </div>
  );
};

const CustomEditor = ({
  handleChange,
  handleBlur,
  handleKeyDown,
  editorContent,
  placeholderText,
  editingRef,
  shouldBlurOnEnter,
  shouldBlurOnShiftEnter,
  isDisabled = false,
  isFullWidth = false,
  isFullHeight = false,
  showMenu = false,
  showAdvancedOptions = false,
  alwaysShowMenu = false,
  forceFocus = false,
  scrollToHiddenAnchor = false,
  sansSerif = false,
  showRightAlign = false,
  showHighlight = false,
  /** branding signature editors: template-string block + toolbar control */
  enableTemplateStringBlock = false,
  id,
}: {
  /** Fired on every edit and selection change. Receives HTML when `showMenu` is true, otherwise plain text. */
  handleChange: (val: string) => void;
  /** Fired when the editor loses focus. Suppressed when focus moves into the menu bar. */
  handleBlur?: () => void;
  /** Forwarded to the underlying `EditorContent` element for custom keyboard handling. */
  handleKeyDown?: (e: React.KeyboardEvent) => void;
  /** When true, the editor is read-only. */
  isDisabled?: boolean;
  /** Controlled value for the editor. Synced into Tiptap when it diverges from the current document. */
  editorContent: string | undefined;
  /** Placeholder shown when the editor is empty. */
  placeholderText?: string | undefined;
  /** Ref to the inner `EditorContent` DOM node, for callers that need to measure or scroll the editor. */
  editingRef?: RefObject<HTMLDivElement | null>;
  /** When true, focuses the editor on mount (and whenever the flag flips true). */
  forceFocus?: boolean;
  /** When true, pressing Enter blurs the editor instead of inserting a newline. Used for single-line fields. */
  shouldBlurOnEnter?: boolean;
  /** When true, pressing Shift+Enter blurs the editor. */
  shouldBlurOnShiftEnter?: boolean;
  /** When true, the editor stretches to 100% of its parent's width. */
  isFullWidth?: boolean;
  /** When true, the editor stretches to 100% of its parent's height. */
  isFullHeight?: boolean;
  /** When true, renders the formatting toolbar and outputs HTML (instead of plain text) via `handleChange`. */
  showMenu?: boolean;
  /** When true (with `showMenu`), enables the extended toolbar: lists, alignment, highlight, template strings. */
  showAdvancedOptions?: boolean;
  /** When true, the toolbar stays visible even when the editor is not focused. */
  alwaysShowMenu?: boolean;
  /**
   * When true, scrolls to `#hidden-anchor` (rendered below the editor) while typing. Useful on pages
   * where the editor can be taller than the viewport and sits behind a fixed footer or similar overlay.
   */
  scrollToHiddenAnchor?: boolean;
  /** When true, uses the sans-serif font; otherwise the editor renders in serif. */
  sansSerif?: boolean;
  /** When true (with `showMenu` + `showAdvancedOptions`), adds the align-right button to the toolbar. */
  showRightAlign?: boolean;
  /** When true, adds the highlight button to the toolbar. */
  showHighlight?: boolean;
  /** Branding signature editors: enables the template-string block extension and its toolbar control. */
  enableTemplateStringBlock?: boolean;
  /** DOM id applied to the `EditorContent` element. */
  id?: string;
}) => {
  const [isMenuVisible, setIsMenuVisible] = useState<boolean>(false);
  const menuBarRef = useRef<HTMLDivElement | null>(null);
  const { user, firm } = useAuth();
  const [, featureFlags] = useConfig();
  const highlightFeatureFlagEnabled = featureFlags()[HIGHLIGHT_FF]
    ? featureFlags()[HIGHLIGHT_FF](user, firm)
    : false;
  const placeholderConfig = Placeholder.configure({
    placeholder: placeholderText ?? "",
  });
  const paragraphConfig = Paragraph.extend({
    parseHTML() {
      if (!showAdvancedOptions) {
        return [{ tag: "p" }];
      }
      if (enableTemplateStringBlock) {
        return [
          {
            tag: "div",
            getAttrs: (element) =>
              element.classList.contains("template-string") ? false : {},
          },
        ];
      }
      return [{ tag: "div" }];
    },
    renderHTML({ HTMLAttributes }) {
      return [
        showAdvancedOptions ? "div" : "p",
        mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
        0,
      ];
    },
  });
  // NOTE: Remove the next two lines once we support HTML for these fields on the backend.
  const extensions = [
    showMenu
      ? StarterKit.configure({ paragraph: false })
      : StarterKit.configure({ bold: false, italic: false, paragraph: false }),
    placeholderConfig,
    Underline,
    ...(highlightFeatureFlagEnabled && showHighlight ? [Highlight] : []),
    paragraphConfig,
  ];
  const additionalExtensions = [
    showMenu
      ? StarterKit.configure({
          paragraph: false,
          orderedList: false,
          listItem: false,
        })
      : StarterKit.configure({
          bold: false,
          italic: false,
          paragraph: false,
          orderedList: false,
          listItem: false,
        }),
    placeholderConfig,
    Underline,
    ...(highlightFeatureFlagEnabled && showHighlight ? [Highlight] : []),
    OrderedList,
    ListItem,
    TextAlign.configure({
      types: showAdvancedOptions ? ["paragraph", "heading"] : ["paragraph"],
      defaultAlignment: "left",
    }),
    ...(enableTemplateStringBlock ? [TemplateStringBlock] : []),
    paragraphConfig,
    TabIndent,
    FlattenAndFilterOnPaste,
  ];
  const determineExtensions = showAdvancedOptions
    ? additionalExtensions
    : extensions;

  // NOTE: Enable the bottom 'extensions' set up once backend supports HTML. It will reenable all necessary formatting functionality and shortcuts.
  // If you want to test the 'showMenu' bar with all formatting functionality, uncomment the below line and comment the top 'kitConfiguration' variable and 'extensions' array.
  // const extensions = [StarterKit, placeholderConfig, Underline, OrderedList, ListItem];

  // If this prop gets passed, if enter gets pressed, the editor is unselected and we keep the updated value intact.
  if (shouldBlurOnEnter) {
    extensions.push(BlurOnEnter);
  }

  if (shouldBlurOnShiftEnter) {
    extensions.push(BlurOnShiftEnter);
  }

  // Editor set up
  const editor = useEditor({
    extensions: determineExtensions,
    editable: !isDisabled,
    onFocus: () => {
      if (showMenu) {
        setIsMenuVisible(true);
      }
    },
    onUpdate: ({ editor }) => {
      handleChange(showMenu ? editor.getHTML() : editor.getText());
    },
    onBlur: ({ event }) => {
      // Fire off a blur function if needed
      if (handleBlur) {
        // Checks if the editor options are in the menu bar, ignores the passed 'handleBlur' function to avoid the menubar from saving any info in the editor when interacted with.
        if (
          menuBarRef.current?.contains(event.relatedTarget as Element | null)
        ) {
          event.stopPropagation();
          return;
        }
        handleBlur();
      }

      if (showMenu) {
        if (
          menuBarRef.current?.contains(event.relatedTarget as Element | null)
        ) {
          event.stopPropagation();
          return;
        }
        setIsMenuVisible(false);
      }
    },
    onSelectionUpdate: ({ editor }) => {
      handleChange(showMenu ? editor.getHTML() : editor.getText());
    },
    content: "",
  });

  useScrollHiddenTiptap({
    editor,
    offset: 100,
    behavior: "smooth",
    debounceDelay: 50,
    scrollToHiddenAnchor,
  });

  useEffect(() => {
    if (editor) {
      const currentContent = showMenu ? editor.getHTML() : editor.getText();
      if (currentContent !== editorContent) {
        editor.commands.setContent(editorContent || "", { emitUpdate: false });
      }

      // Forces the editor to be focused, useful in cases where we want to focus the editor immediately in a component when immediately loaded to the UI
      // Check if editor view is available before focusing
      if (forceFocus && editor.view) {
        editor.commands.focus();
      }
    }
  }, [editor, forceFocus, editorContent, showMenu]);

  useEffect(() => {
    if (editor) {
      editor.setEditable(!isDisabled);
    }
  }, [editor, isDisabled]);

  // Prevent rendering until the editor is initialized (recommended by Tiptap docs)
  if (!editor) {
    return null;
  }

  return (
    /*If we pass fullwidth, the editor just basically stretches to 100% width of its parent container*/
    <div
      className={`editorContainer ${isFullWidth ? "fw" : ""} ${isFullHeight ? "fh" : ""} ${sansSerif ? "sans-serif" : ""}`}
    >
      {showMenu && (alwaysShowMenu || isMenuVisible) && (
        <MenuBar
          menuBarRef={menuBarRef as RefObject<HTMLDivElement>}
          editor={editor}
          showMenuOptions={showAdvancedOptions}
          showHighlight={highlightFeatureFlagEnabled && showHighlight}
          showRightAlign={showRightAlign}
          showTemplateStringBlock={enableTemplateStringBlock}
        />
      )}
      <EditorContent
        id={id ?? ""}
        className={`${showMenu && (alwaysShowMenu || isMenuVisible) ? "menuOn" : ""}`}
        ref={editingRef}
        onKeyDown={handleKeyDown}
        editor={editor}
      />
      {scrollToHiddenAnchor && <div id="hidden-anchor" />}
    </div>
  );
};

export default CustomEditor;
